import { PreviewTransaction } from '../types';

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
}

export interface ParsedCSV {
  header: string;
  transactions: PreviewTransaction[];
}

export function parseCSVContent(csvContent: string): ParsedCSV {
  const lines = csvContent.trim().split('\n');
  if (lines.length < 2) {
    throw new Error('CSV file appears to be empty or invalid');
  }

  const header = lines[0];
  const headerColumns = parseCSVLine(header);
  const columnMap: Record<string, number> = {};

  headerColumns.forEach((columnName, index) => {
    columnMap[columnName.toLowerCase().trim()] = index;
  });

  const getAmountColumnIndex = (): number | undefined => {
    if (columnMap.amount !== undefined) {
      return columnMap.amount;
    }
    if (columnMap.source_amount !== undefined) {
      return columnMap.source_amount;
    }
    const amountWithCurrency = Object.entries(columnMap).find(([name]) =>
      /^amount\s*\(/.test(name)
    );
    return amountWithCurrency?.[1];
  };

  const amountColumnIndex = getAmountColumnIndex();
  const transactions: PreviewTransaction[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const columns = parseCSVLine(line);

    const getColumnValue = (columnName: string): string | null => {
      const index = columnMap[columnName];
      return index !== undefined ? columns[index] : null;
    };

    let amountValue =
      amountColumnIndex !== undefined ? columns[amountColumnIndex] : null;
    const amount = amountValue
      ? amountValue.toString().replace(/[^-\d.]/g, '') || '0'
      : '0';

    transactions.push({
      id: String(transactions.length),
      date: getColumnValue('date') || '',
      description: getColumnValue('description') || '',
      payee: getColumnValue('payee') || '',
      amount,
      sourceAmount: getColumnValue('source_amount') || undefined,
      sourceCurrency: getColumnValue('source_currency') || undefined,
      exchangeRate: getColumnValue('exchange_rate') || undefined,
      category: getColumnValue('category') || undefined,
      categoryId: getColumnValue('categoryid') || undefined,
      categoryName: getColumnValue('category') || undefined,
      originalLine: line,
      selected: true,
    });
  }

  return { header, transactions };
}

export function buildFilteredCsv(
  header: string,
  transactions: PreviewTransaction[]
): string {
  const selectedLines = transactions
    .filter((transaction) => transaction.selected)
    .map((transaction) => transaction.originalLine);

  if (selectedLines.length === 0) {
    return header;
  }

  return `${header}\n${selectedLines.join('\n')}`;
}
